import { Box, Container, Grid, Typography, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Reveal } from "src/theme/animation";

const STEPS = ["Assess", "Reflect", "Apply", "Observe", "Measure"] as const;

const SLIDES = [
    {
        src: "/images/how-it-works/01-Pre-Assessment.webp",
        alt: "Pre-Assessment — start with the athlete's voice"
    },
    {
        src: "/images/how-it-works/02-Feedback-Report.webp",
        alt: "Feedback Report — turn insight into shared language"
    },
    {
        src: "/images/how-it-works/03-Lesson-Plans.webp",
        alt: "Lesson Plans — train skills in-app"
    },
    {
        src: "/images/how-it-works/04-Post-Assessment.webp",
        alt: "Post-Assessment — measure growth over time"
    }
] as const;

/** Matches Squarespace homepage gallery autoplay (seconds). */
const AUTOPLAY_SECONDS = 8;

/** Soft blue used for the process-flow arrows on Squarespace. */
const ARROW_COLOR = "#8EB6DC";

/**
 * Marketing "How it Works" section matching the Squarespace homepage:
 * copy + Assess→Measure flow on the left, autoplaying product slideshow on the right.
 */
const HowItWorksSection: React.FC = () => {
    const theme = useTheme();
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const id = window.setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % SLIDES.length);
        }, AUTOPLAY_SECONDS * 1000);
        return () => window.clearInterval(id);
    }, []);

    return (
        <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: "background.paper" }}>
            <Container maxWidth="lg">
                <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <Reveal variant="fadeUp">
                            <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                mb: 2.5,
                                fontWeight: 800,
                                color: "primary.main",
                                fontSize: { xs: "1.75rem", md: "1.9rem" }
                            }}
                        >
                            How it Works
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 2,
                                lineHeight: 1.7,
                                fontSize: "1.05rem",
                                color: "text.primary"
                            }}
                        >
                            Confidence isn’t something you either have or don’t—it’s something you build.
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 3.5,
                                lineHeight: 1.7,
                                fontSize: "1.05rem",
                                color: "text.primary"
                            }}
                        >
                            Strong Mind Strong Performance™ participants identify strengths, practice
                            practical mental skills like focus and composure, and receive multi-layered
                            feedback from supporters that helps confirm real-world growth—creating an
                            asset-based system that builds confidence from the inside out.
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: { xs: 0.75, sm: 1 },
                                rowGap: 1
                            }}
                            aria-label="Process: Assess, Reflect, Apply, Observe, Measure"
                        >
                            {STEPS.map((step, index) => (
                                <React.Fragment key={step}>
                                    {index > 0 && (
                                        <Typography
                                            component="span"
                                            aria-hidden
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: { xs: "1.25rem", md: "1.6rem" },
                                                color:
                                                    theme.palette.mode === "light"
                                                        ? ARROW_COLOR
                                                        : "primary.main",
                                                lineHeight: 1
                                            }}
                                        >
                                            →
                                        </Typography>
                                    )}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "1.25rem", md: "1.6rem" },
                                            color: "text.primary",
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {step}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                        </Reveal>
                    </Grid>


                    <Grid item xs={12} md={7}>
                        <Reveal variant="scaleIn" delay={0.15}>
                            <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: { xs: 3, md: 2 }
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    flex: "1 1 280px",
                                    minWidth: 0,
                                    // 4:3 slides from Squarespace
                                    aspectRatio: "4 / 3",
                                    overflow: "hidden"
                                }}
                            >
                                {SLIDES.map((slide, index) => (
                                    <Box
                                        key={slide.src}
                                        component="img"
                                        src={slide.src}
                                        alt={index === activeIndex ? slide.alt : ""}
                                        aria-hidden={index !== activeIndex}
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                            opacity: index === activeIndex ? 1 : 0,
                                            transition: "opacity 0.8s ease-in-out",
                                            pointerEvents: "none"
                                        }}
                                    />
                                ))}
                            </Box>
                            <Box
                                component="img"
                                src="/images/evidence-loop.png"
                                alt="The SMSP Evidence Loop — Multi-Source Growth Verification"
                                sx={{
                                    flex: "1 1 220px",
                                    width: "100%",
                                    maxWidth: { xs: 360, md: "100%" },
                                    height: "auto",
                                    objectFit: "contain"
                                }}
                            />
                        </Box>
                        </Reveal>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default HowItWorksSection;
